/**
 * 90-day weight-loss projection for the Fit Quiz.
 *
 * 12-week mean %-body-weight loss targets (clinically calibrated, assume
 * standard 4-week titration per FDA label):
 *   - Sema: low=5.0%, mid=6.5%, high=7.5%  (STEP-1 week-12 during titration)
 *   - Tirz: low=6.0%, mid=8.0%, high=10.0% (SURMOUNT-1 5/10/15 mg wk-12)
 *
 * Real-world 3-mo tirzepatide median ~7.1% (2024 clinical study, n=117) —
 * numbers below are tuned to match the published trial + real-world window.
 *
 * Curve: saturating exponential scaled so pct(12) = target. Matches the
 * clinical pattern of slow ramp during titration → therapeutic plateau.
 *
 * IMPORTANT: any change to these numbers must also be mirrored in
 * `backend/services/fit_quiz_chart.py` (email PNG renderer).
 */
import React, { useState } from "react";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ReferenceDot, CartesianGrid } from "recharts";
import { BookOpen, ChevronDown, ChevronUp } from "lucide-react";

export const TWELVE_WEEK_PCT = {
  "sema-low":  5.0,
  "sema-mid":  6.5,
  "sema-high": 7.5,
  "tirz-low":  6.0,
  "tirz-mid":  8.0,
  "tirz-high": 10.0,
};

function buildCurve(startWeight, drug, dose) {
  const target = TWELVE_WEEK_PCT[`${drug}-${dose}`] || 0;
  const weeks = 13;
  // Scale so the saturating-exponential exactly hits `target` at week 12.
  const denom = 1 - Math.exp(-12 / 6);
  const scaled = denom > 0 ? target / denom : 0;
  const data = [];
  for (let w = 0; w <= weeks; w++) {
    const pct = scaled * (1 - Math.exp(-w / 6));
    const projected = startWeight * (1 - pct / 100);
    data.push({
      week: w,
      weight: Math.round(projected * 10) / 10,
      label: w === 0 ? "Today" : `Wk ${w}`,
    });
  }
  return { data, peakPct: target };
}

export default function FitQuizProjection({ startWeight, drug, dose, actualPoints = [] }) {
  const [showSources, setShowSources] = useState(false);
  if (!startWeight || !drug || !dose) return null;

  const { data, peakPct } = buildCurve(startWeight, drug, dose);
  // Merge actual weigh-ins onto the same week-indexed dataset so recharts
  // can plot them as a separate line on top of the projection.
  const byWeek = Object.fromEntries(actualPoints.map((p) => [p.week, p.weight]));
  const merged = data.map((d) => ({ ...d, actual: byWeek[d.week] ?? null }));
  const finalWeight = data[data.length - 1].weight;
  const lost = Math.round((startWeight - finalWeight) * 10) / 10;
  const pctR = Math.round(peakPct * 10) / 10;
  // Latest actual weight (for the "you are here" callout)
  const latestActual = actualPoints.length > 0 ? actualPoints[actualPoints.length - 1] : null;

  return (
    <div className="rounded-xl border border-purple-100 dark:border-purple-900/40 bg-gradient-to-br from-white via-purple-50/30 to-white dark:from-slate-900 dark:via-purple-950/30 dark:to-slate-900 p-4" data-testid="fit-quiz-projection">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-purple-700 dark:text-purple-300 font-semibold mb-0.5">Projected 90-day trajectory</p>
          <p className="text-lg font-bold text-slate-900 dark:text-white leading-tight">
            ~<span className="text-purple-700 dark:text-purple-300">{finalWeight} lb</span>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300"> at week 13</span>
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            That&apos;s about <strong className="text-emerald-700 dark:text-emerald-300">{lost} lb ({pctR}%)</strong> off your current weight.
          </p>
          {latestActual && (
            <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-1.5">
              <strong>You at week {latestActual.week}:</strong> {latestActual.weight} lb
            </p>
          )}
        </div>
      </div>

      <div className="h-32 -mx-2" data-testid="fit-quiz-projection-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={merged} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="fqpGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
              interval={2}
            />
            <YAxis
              domain={[Math.floor(finalWeight - 3), Math.ceil(startWeight + 2)]}
              tick={{ fontSize: 10, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
              formatter={(v, name) => [`${v} lb`, name === "actual" ? "Actual" : "Projected"]}
              labelFormatter={(l) => `${l}`}
            />
            <Area type="monotone" dataKey="weight" stroke="#7c3aed" strokeWidth={2} fill="url(#fqpGrad)" />
            {actualPoints.length > 0 && (
              <Line type="monotone" dataKey="actual" stroke="#059669" strokeWidth={2.25} dot={{ r: 3, fill: "#059669", stroke: "#fff", strokeWidth: 1.5 }} connectNulls />
            )}
            <ReferenceDot x="Today" y={startWeight} r={4} fill="#0f172a" stroke="#fff" strokeWidth={2} />
            <ReferenceDot x={`Wk 13`} y={finalWeight} r={4} fill="#7c3aed" stroke="#fff" strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {actualPoints.length > 0 && (
        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 dark:text-slate-400">
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-purple-600 rounded" /> Projected</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-0.5 bg-emerald-600 rounded" /> Your actual</span>
        </div>
      )}

      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2 leading-relaxed">
        Projection based on published clinical trial averages (STEP-1 for Semaglutide,
        SURMOUNT-1 for Tirzepatide), scaled to your starting dose. Your actual
        results depend on diet, activity, adherence, and physiology — individual
        results vary.
      </p>

      <button
        type="button"
        onClick={() => setShowSources((s) => !s)}
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-purple-700 dark:text-purple-300 hover:text-purple-900 dark:hover:text-purple-200 font-semibold hover:underline underline-offset-2"
        data-testid="fit-quiz-sources-toggle"
        aria-expanded={showSources}
      >
        <BookOpen className="w-3 h-3" />
        {showSources ? "Hide sources" : "Show sources"}
        {showSources ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {showSources && (
        <div className="mt-2 rounded-lg border border-purple-100 dark:border-purple-900/40 bg-white/70 dark:bg-slate-900/60 px-3 py-2.5 space-y-2 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300" data-testid="fit-quiz-sources-list">
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100 mb-0.5">STEP-1 — Semaglutide 2.4 mg weekly</p>
            <p>Wilding JPH et al. <em>N Engl J Med</em> 2021;384:989–1002 — 6–8% mean body-weight loss during week-12 titration, progressing to 14.9% at week 68.{" "}
              <a href="https://www.nejm.org/doi/full/10.1056/NEJMoa2032183" target="_blank" rel="noopener noreferrer" className="text-purple-700 dark:text-purple-300 hover:underline">NEJM.org</a>
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100 mb-0.5">SURMOUNT-1 — Tirzepatide weekly</p>
            <p>Jastreboff AM et al. <em>N Engl J Med</em> 2022;387:205–216 — week-12 mean: 5.2% (5 mg), 7.9% (10 mg), 9.7% (15 mg). 72-week: 15.0/19.5/20.9%.{" "}
              <a href="https://www.nejm.org/doi/full/10.1056/NEJMoa2206038" target="_blank" rel="noopener noreferrer" className="text-purple-700 dark:text-purple-300 hover:underline">NEJM.org</a>
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100 mb-0.5">Real-world cohort (2022–2024, n = 2,306)</p>
            <p>Multidisciplinary weight-management clinic. Tirzepatide users: median 7.1% total body-weight loss at 3 months (IQR 4.4–9.7%).{" "}
              <a href="https://pubmed.ncbi.nlm.nih.gov/40762026/" target="_blank" rel="noopener noreferrer" className="text-purple-700 dark:text-purple-300 hover:underline">PubMed 40762026</a>
            </p>
          </div>
          <p className="text-[10px] text-slate-400 dark:text-slate-500 pt-1 border-t border-slate-100 dark:border-slate-800">
            Our curves assume FDA-label titration (4-week dose escalation). Every
            signup is reviewed by a US-licensed provider — the prescribed plan
            may differ from this preview.
          </p>
        </div>
      )}
    </div>
  );
}
